import { useCallback, useEffect, useRef, useState } from 'react';
import { IconRefresh, IconShieldCheck, IconEye, IconLoader2 } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import { StatCard } from '../components/cards';
import MainCard from '../components/MainCard';

type VerificationTab = 'all' | 'verified' | 'not_verified';

interface VerificationRow {
  sub_distributor_id: number;
  distributor_i_customer: number;
  entity_name: string;
  distributor_company_name: string | null;
  details_verified: boolean;
  level_label: string;
}

interface VerificationCounts {
  total: number;
  verified: number;
  notVerified: number;
}

interface VerificationPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface VerificationResponse {
  success: boolean;
  data: VerificationRow[];
  counts: VerificationCounts;
  pagination?: VerificationPagination;
}

interface DistributorOption {
  i_customer: number;
  company_name: string | null;
}

export function DistributorVerificationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VerificationRow[]>([]);
  const [counts, setCounts] = useState<VerificationCounts | null>(null);
  const [pagination, setPagination] = useState<VerificationPagination | null>(null);
  const [distributorOptions, setDistributorOptions] = useState<DistributorOption[]>([]);
  const [tab, setTab] = useState<VerificationTab>('all');
  const [distributorFilter, setDistributorFilter] = useState<number | ''>('');
  const [entityNameSearch, setEntityNameSearch] = useState('');
  const [distributorOptionsLoaded, setDistributorOptionsLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback(() => {
    if (searchDebounceTimerRef.current) clearTimeout(searchDebounceTimerRef.current);
    searchDebounceTimerRef.current = setTimeout(() => {
      const value = searchInputRef.current?.value?.trim() ?? '';
      setEntityNameSearch(value);
      setPage(0);
      searchDebounceTimerRef.current = null;
    }, 350);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) clearTimeout(searchDebounceTimerRef.current);
    };
  }, []);

  const SPAR_DISTRIBUTOR_NAME = 'The SPAR Group Limited';

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ success: boolean; data: DistributorOption[] }>('/distributors/verification-status/distributors')
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.data)) {
          const options = res.data.data;
          setDistributorOptions(options);
          const spar = options.find((d) => d.company_name === SPAR_DISTRIBUTOR_NAME);
          if (spar) setDistributorFilter(spar.i_customer);
          setDistributorOptionsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setDistributorOptionsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchVerificationStatus = useCallback(
    async (opts: {
      distributorId?: number;
      page: number;
      limit: number;
      tab: VerificationTab;
      search: string;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          page: opts.page + 1,
          limit: opts.limit,
          tab: opts.tab,
          search: opts.search,
        };
        if (opts.distributorId != null) params.distributorId = opts.distributorId;
        const res = await api.get<VerificationResponse>('/distributors/verification-status', {
          params,
        });
        const body: VerificationResponse =
          res.data != null && typeof res.data === 'object'
            ? (res.data as VerificationResponse)
            : {
                success: true,
                data: [],
                counts: { total: 0, verified: 0, notVerified: 0 },
                pagination: { page: 1, limit: 25, total: 0, pages: 0 },
              };
        const list = Array.isArray(body.data) ? body.data : [];
        setData(list);
        setCounts(body.counts ?? null);
        setPagination(body.pagination ?? null);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(msg || 'Failed to load verification status');
        setData([]);
        setCounts(null);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!distributorOptionsLoaded) return;
    fetchVerificationStatus({
      distributorId: distributorFilter === '' ? undefined : Number(distributorFilter),
      page,
      limit: rowsPerPage,
      tab,
      search: entityNameSearch,
    });
  }, [fetchVerificationStatus, distributorOptionsLoaded, distributorFilter, page, rowsPerPage, tab, entityNameSearch]);

  const totalRows = pagination?.total ?? 0;
  const totalPages = pagination?.pages ?? 0;

  const rowsPerPageOptions = [10, 25, 50, 100, 250];

  const handleCompleteVerification = (row: VerificationRow) => {
    navigate(
      `/spar-mobile/store-compliance/complete/${row.distributor_i_customer}/${row.sub_distributor_id}?mode=complete`,
      {
        state: {
          distributor_i_customer: row.distributor_i_customer,
          sub_distributor_id: row.sub_distributor_id,
          entity_name: row.entity_name,
          mode: 'complete' as const,
        },
      },
    );
  };

  const handleViewDetails = (row: VerificationRow) => {
    navigate(
      `/spar-mobile/store-compliance/complete/${row.distributor_i_customer}/${row.sub_distributor_id}?mode=view`,
      {
        state: {
          distributor_i_customer: row.distributor_i_customer,
          sub_distributor_id: row.sub_distributor_id,
          entity_name: row.entity_name,
          mode: 'view' as const,
        },
      },
    );
  };

  return (
    <div>
      <PageHeader
        title="Store Compliance"
        subtitle="Level 3 sub-distributor verification status for SPAR Mobile. Unverified sub-distributors are blocked from top-ups in the portal."
        action={
          <Button
            variant="outline"
            onClick={() =>
              fetchVerificationStatus({
                distributorId: distributorFilter === '' ? undefined : Number(distributorFilter),
                page,
                limit: rowsPerPage,
                tab,
                search: entityNameSearch,
              })
            }
            disabled={loading}
          >
            <IconRefresh size={16} />
            Refresh
          </Button>
        }
      />

      {counts && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 min-w-0">
            <StatCard title="Total (Level 3)" value={counts.total.toLocaleString()} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Verified" value={counts.verified.toLocaleString()} />
          </div>
          <div className="flex-1 min-w-0">
            <StatCard title="Not verified" value={counts.notVerified.toLocaleString()} />
          </div>
          {counts.total > 0 && counts.verified + counts.notVerified > 0 && (
            <div className="flex-1 min-w-0">
              <StatCard
                title="% verified"
                value={`${((counts.verified / (counts.verified + counts.notVerified)) * 100).toFixed(1)}%`}
              />
            </div>
          )}
        </div>
      )}

      <MainCard className="mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="inline-flex rounded-lg border border-border p-0.5">
            {(['all', 'verified', 'not_verified'] as VerificationTab[]).map((t) => (
              <button
                key={t}
                onClick={() => { setTab(t); setPage(0); }}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  tab === t
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {t === 'all' ? 'All' : t === 'verified' ? 'Verified' : 'Not verified'}
              </button>
            ))}
          </div>
          <Input
            ref={searchInputRef}
            placeholder="Entity name or parent distributor..."
            onChange={handleSearchChange}
            className="min-w-[240px] max-w-sm"
            aria-label="Search by entity name or parent distributor"
          />
        </div>
      </MainCard>

      <MainCard>
        {loading && (
          <div className="flex justify-center py-12">
            <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && error && (
          <Alert variant="destructive" className="m-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Verification status</TableHead>
                  <TableHead>Distributor level</TableHead>
                  <TableHead>Entity name</TableHead>
                  <TableHead>Parent distributor</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => (
                  <TableRow
                    key={row.sub_distributor_id}
                    className={cn(!row.details_verified && 'bg-muted/30')}
                  >
                    <TableCell>
                      {row.details_verified ? (
                        <Badge variant="outline" className="text-green-700 border-green-300">
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">
                          Not verified
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{row.level_label}</TableCell>
                    <TableCell>{row.entity_name || '—'}</TableCell>
                    <TableCell>{row.distributor_company_name ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      {row.details_verified ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(row)}
                        >
                          <IconEye size={14} />
                          View details
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCompleteVerification(row)}
                        >
                          <IconShieldCheck size={14} />
                          Complete verification
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <div className="py-8 text-center">
                        <p className="text-muted-foreground">
                          No Level 3 sub-distributors match the current filter.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Rows per page:</span>
                <Select
                  value={String(rowsPerPage)}
                  onValueChange={(val) => {
                    if (val) {
                      const parsed = parseInt(val, 10);
                      const next = Number.isFinite(parsed) && rowsPerPageOptions.includes(parsed) ? parsed : 25;
                      setRowsPerPage(next);
                      setPage(0);
                    }
                  }}
                >
                  <SelectTrigger className="w-[70px]" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rowsPerPageOptions.map((opt) => (
                      <SelectItem key={opt} value={String(opt)}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>
                  {totalRows > 0 ? `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, totalRows)} of ${totalRows}` : '0 results'}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setPage(0)} disabled={page === 0}>
                    First
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                    Prev
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                    Next
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
                    Last
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </MainCard>
    </div>
  );
}
